import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import moment from 'moment';
import * as XLSX from 'xlsx';
import { lastValueFrom } from 'rxjs';

import { DynamicListingComponent, FilterConfig, ColumnConfig, ActionConfig, ActionEvent, FilterEvent } from '../dynamic-listing/dynamic-listing';
import { ClientListService } from '../services/client/client-list.service';
import { ClientPaymentService } from '../services/client/client-payment.service';
import { ToasterService } from '@ui-nuvertos/auth';
import { SVG_ICONS } from '../utils/svg-icons.utils';

interface ClientRefundRow {
  remarks?: string;
  id: string;
  transaction_no: string;
  refund_date: string;
  client_id: string;
  client_name: string;
  refund_amount: number;
  refund_method: string;
  reason: string;
  status: string;
  _original?: Record<string, unknown>;
}

@Component({
  selector: 'lib-client-refund-list',
  standalone: true,
  imports: [CommonModule, DynamicListingComponent],
  templateUrl: './client-refund-list.html',
  styleUrls: ['./client-refund-list.css'],
})
export class ClientRefundList implements OnInit {
  isLoading = false;
  isLoadingClients = false;
  exportFilename = `client_refunds_${new Date().toISOString().slice(0, 10)}`;

  tableData: ClientRefundRow[] = [];
  totalAmount = 0;

  // Filters
  refundDate: Date | null = null;
  client = '';
  status = '';

  // Filter configurations
  filterConfigs: FilterConfig[] = [];

  // Pagination
  pageIndex = 1;
  rowsPerPage = 10;
  totalCount = 0;
  totalPages = 0;

  // Filter options
  clientOptions: { label: string; value: string }[] = [];
  private clientLabelMap = new Map<string, string>();
  statusOptions: any[] = [];

  // Column configuration for dynamic listing
  columnConfigs: ColumnConfig[] = [
    { key: 'transaction_no', header: 'Transaction No.' },
    { key: 'refund_date', header: 'Refund Date', type: 'date' },
    { key: 'client_name', header: 'Client Name', ellipsis: true },
    { key: 'refund_amount', header: 'Refund Amount', type: 'currency' },
    { key: 'refund_method', header: 'Refund Method' },
    { key: 'reason', header: 'Reason', ellipsis: true },
    { key: 'status', header: 'Status', type: 'status' },
  ];

  actionConfigs: ActionConfig[] = [
    {
      key: 'view',
      label: 'View',
      tooltip: 'View refund',
      severity: 'secondary',
      svgIcon: SVG_ICONS.VIEW,
    },
    {
      key: 'edit',
      label: 'Edit',
      tooltip: 'Edit refund',
      severity: 'secondary',
      svgIcon: SVG_ICONS.EDIT,
    },
    {
      key: 'delete',
      label: 'Delete',
      tooltip: 'Delete refund',
      severity: 'secondary',
      svgIcon: SVG_ICONS.DELETE,
    },
  ];

  headerActionConfigs: ActionConfig[] = [];

  constructor(
    private router: Router,
    private clientService: ClientListService,
    private clientPaymentService: ClientPaymentService,
    private toasterService: ToasterService
  ) {}

  async ngOnInit() {
    await this.loadClients();
    this.initializeFilterConfigs();
    await this.loadClientRefunds();
  }

  initializeFilterConfigs(): void {
    this.filterConfigs = [
      {
        key: 'refundDate',
        type: 'date',
        placeholder: 'Refund Date',
        value: this.refundDate,
        showInAccordion: true,
      },
      {
        key: 'client',
        type: 'select',
        placeholder: 'Client',
        options: this.clientOptions,
        optionLabel: 'label',
        optionValue: 'value',
        value: this.client,
        loading: this.isLoadingClients,
        showInAccordion: true,
      },
      {
        key: 'status',
        type: 'select',
        placeholder: 'Status',
        options: this.statusOptions,
        optionLabel: 'label',
        optionValue: 'value',
        value: this.status,
        showInAccordion: true,
      },
    ];
  }

  async loadClients(): Promise<void> {
    if (this.isLoadingClients) return;

    try {
      this.isLoadingClients = true;
      const response = await lastValueFrom(
        this.clientService.getClients({ limit: 1000, type: 'all' })
      );

      const clients = response?.data?.clients ?? [];
      this.clientOptions = clients.map((client: any) => {
        const id = String(client.id ?? '');
        const label = client.client_name || client.name || '-';
        if (id) {
          this.clientLabelMap.set(id, label);
        }
        return {
          label,
          value: id,
        };
      });
    } catch (error) {
      console.error('Error loading clients:', error);
      this.clientOptions = [];
    } finally {
      this.isLoadingClients = false;
      this.initializeFilterConfigs();
    }
  }

  async loadClientRefunds(page = 1): Promise<void> {
    try {
      this.isLoading = true;
      this.pageIndex = page;

      const filters: any = {
        limit: this.rowsPerPage,
        offset: (this.pageIndex - 1) * this.rowsPerPage,
        is_refund: true, // Filter for refund payments only
      };

      if (this.refundDate) {
        filters.refund_date = this.formatDateForApi(this.refundDate);
      }

      if (this.client) {
        filters.client_id = this.client;
      }

      if (this.status) {
        filters.status = this.status;
      }

      // Use dedicated refund API endpoint
      const response = await lastValueFrom(this.clientPaymentService.getClientRefunds(filters));

      const rawData: any[] = this.extractRefundArray(response);
      this.tableData = rawData.map(item => this.mapRefundRow(item));

      this.totalCount = response?.data?.total;
      this.totalPages = Math.max(1, Math.ceil(this.totalCount / this.rowsPerPage));
      this.totalAmount = this.tableData.reduce((sum, refund) => sum + (refund.refund_amount || 0), 0);
    } catch (error) {
      console.error('Error loading client refunds:', error);
      this.toasterService.error('Error', 'Failed to load client refunds');
      this.tableData = [];
      this.totalCount = 0;
      this.totalPages = 0;
      this.totalAmount = 0;
    } finally {
      this.isLoading = false;
    }
  }

  private extractRefundArray(response: any): any[] {
    if (!response?.data) {
      return [];
    }

    // Handle refund API response structure
    if (Array.isArray(response.data.client_refund)) {
      return response.data.client_refund;
    }


    return [];
  }

  private mapRefundRow(item: any): ClientRefundRow {
    // Handle refund API response structure - refund data might be directly in item or nested
    const refund = item?.client_refund || item?.refund || item?.client_payment || item || {};
    
    return {
      id: refund.id || item?.id || '',
      transaction_no: refund.transaction_no || item?.transaction_no || '-',
      refund_date: refund.refund_date || refund.payment_date || item?.refund_date || item?.payment_date || '',
      client_id: refund.client_id || item?.client_id || '-',
      client_name:
        this.clientLabelMap.get(String(refund.client_id ?? item?.client_id ?? '')) ||
        refund.client_name ||
        refund.client?.name ||
        item?.client_name ||
        item?.client?.name ||
        '-',
      refund_amount: Number(
        refund.refund_amount ?? 
        refund.total_refund_amount ?? 
        refund.total_receivable_amount ?? 
        refund.advance_amount ?? 
        item?.refund_amount ?? 
        0
      ),
      refund_method: refund.refund_method || refund.payment_method || item?.refund_method || item?.payment_method || 'Refund',
      reason: refund.remarks || refund.memo || refund.notes || item?.reason || item?.memo || item?.notes || 'No reason provided',
      status: this.getStatusString(refund.status ?? item?.status),
      _original: refund,
    };
  }

  onFilterEvent(event: FilterEvent): void {
    const data = event.data ?? {};
    this.refundDate = data.refundDate ?? null;
    this.client = data.client ?? '';
    this.status = data.status ?? '';

    this.pageIndex = 1;
    this.initializeFilterConfigs();
    this.loadClientRefunds(this.pageIndex);
  }

  onColumnsSelected(_: string[]): void {
    // Placeholder for future column preference persistence
  }

  onActionClicked(event: ActionEvent): void {
    if (event.row === null) {
      // Header action
      switch (event.action) {
        case 'createRefund':
          this.createRefund();
          break;
        case 'bulkUpload':
          this.bulkUpload();
          break;
      }
      return;
    }

    const refundId = event.row?.id;
    if (!refundId) return;

    switch (event.action) {
      case 'view':
        this.view(refundId);
        break;
      case 'edit':
        this.edit(refundId);
        break;
      case 'delete':
        this.delete(refundId);
        break;
    }
  }

  onPageChanged(page: number): void {
    this.loadClientRefunds(page);
  }

  exportCsv(): void {
    if (!this.tableData.length) {
      this.toasterService.info('Info', 'No data available to export.');
      return;
    }

    const headers = [
      'S.No.',
      'Transaction No.',
      'Refund Date',
      'Client Name',
      'Refund Amount',
      'Refund Method',
      'Reason',
      'Status',
    ] as const;

    type ExportRow = { [K in typeof headers[number]]: string | number };

    const rows: ExportRow[] = this.tableData.map((row, index) => ({
      'S.No.': (this.pageIndex - 1) * this.rowsPerPage + index + 1,
      'Transaction No.': row.transaction_no ?? '-',
      'Refund Date': this.formatDisplayDate(row.refund_date),
      'Client Name': row.client_name ?? '-',
      'Refund Amount': row.refund_amount ?? 0,
      'Refund Method': row.refund_method ?? '-',
      'Reason': row.remarks ?? '-',
      'Status': row.status ?? '-',
    }));

    const csvContent = [
      headers.join(','),
      ...rows.map(row =>
        headers
          .map(header => {
            const cell = row[header] ?? '';
            const escaped = cell.toString().replace(/"/g, '""');
            return `"${escaped}"`;
          })
          .join(',')
      ),
    ].join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${this.exportFilename}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  exportXlsx(): void {
    if (!this.tableData.length) {
      this.toasterService.info('Info', 'No data available to export.');
      return;
    }

    const rows = this.tableData.map((row, index) => ({
      'S.No.': (this.pageIndex - 1) * this.rowsPerPage + index + 1,
      'Transaction No.': row.transaction_no ?? '-',
      'Refund Date': this.formatDisplayDate(row.refund_date),
      'Client Name': row.client_name ?? '-',
      'Refund Amount': row.refund_amount ?? 0,
      'Refund Method': row.refund_method ?? '-',
      'Reason': row.reason ?? '-',
      'Status': row.status ?? '-',
    }));

    const worksheet = XLSX.utils.json_to_sheet(rows);
    worksheet['!cols'] = Array(rows.length ? Object.keys(rows[0]).length : 0).fill({ wch: 18 });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Client Refunds');
    XLSX.writeFile(workbook, `${this.exportFilename}.xlsx`);
  }

  exportPdf(): void {
    window.print();
  }

  view(refundId: string): void {
    this.router.navigate(['/clients/refund-payment'], {
      queryParams: { clientRefundId: refundId, mode: 'view' },
    });
  }

  edit(refundId: string): void {
    this.router.navigate(['/clients/refund-payment'], {
      queryParams: { clientRefundId: refundId, mode: 'edit' },
    });
  }

  delete(refundId: string): void {
    if (!confirm('Are you sure you want to delete this refund?')) {
      return;
    }

    // Use dedicated refund API endpoint
    this.clientPaymentService.deleteClientRefund(refundId).subscribe({
      next: () => {
        this.toasterService.success('Success', 'Refund deleted successfully');
        this.loadClientRefunds();
      },
      error: (error) => {
        const message = error?.error?.message || error?.message || 'Failed to delete refund';
        this.toasterService.error('Error', message);
      },
    });
  }

  createRefund(): void {
    this.router.navigate(['/clients/refund-payment']);
  }

  bulkUpload(): void {
    // TODO: Implement bulk upload functionality
    this.toasterService.info('Info', 'Bulk upload functionality coming soon.');
  }

  private formatDateForApi(date: Date): string {
    return moment(date).format('YYYY-MM-DD');
  }

  private formatDisplayDate(value: string): string {
    return value ? moment(value).format('DD-MM-YYYY') : '-';
  }

  private getStatusString(status: number | string | undefined): string {
    if (status === undefined || status === null) return 'Pending';
    if (typeof status === 'string') return status;

    const statusMap: Record<number, string> = {
      0: 'Pending',
      1: 'Processed',
      2: 'Refunded',
      3: 'Cancelled',
    };

    return statusMap[status] || 'Pending';
  }
}
